import type { SlowInteraction, SlowInteractionGroup } from '../analytics/interactivity-snapshot';

import type { InteractionEntry } from './interaction-tracker';

/** Fixed by the schema, so the event stays a bounded size. */
const MAX_RECORDS = 5;

/**
 * The latency an interaction has to beat to be recorded. 200 ms is the Google INP "good" threshold,
 * so anything below it is an interaction the user was not waiting for.
 */
const MIN_LATENCY_MS = 200;

/**
 * The attributes a target may be named by, all of them ours: `data-vc` for visual completion, the
 * test ids for tests. Ids, roles, class names, text and accessibility labels are left out because
 * they can carry what the user wrote.
 */
const ALLOWED_TARGET_ATTRIBUTES = ['data-vc', 'data-testid', 'data-test-id'];

const MAX_TARGET_ELEMENTS = 4;
const MAX_ATTRIBUTE_VALUE_LENGTH = 32;
const MAX_TARGET_LENGTH = 120;

/** The id identifies an interaction across the entries measuring it. It is not reported. */
type RecordedInteraction = SlowInteraction & { interactionId: number };

/** An interaction as the collector's two observers saw it. */
type ObservedInteraction = {
	entry: InteractionEntry;
	group: SlowInteractionGroup;
	interactionId: number;
	/** The duration of the entry measuring the interaction at its slowest. */
	latencyMs: number;
};

/**
 * The slowest interactions of one session, which is what the event's `slowest` records are.
 *
 * A record is built from the entry that measured the interaction, as that entry arrives:
 * `entry.target` is `null` once the element has left the document.
 */
export class SlowInteractionList {
	/** Slowest first. */
	private readonly records: RecordedInteraction[] = [];

	track(interaction: ObservedInteraction): void {
		const { interactionId, latencyMs } = interaction;

		const previousIndex = this.records.findIndex(
			(existing) => existing.interactionId === interactionId,
		);

		let toBeatMs = MIN_LATENCY_MS;
		if (previousIndex !== -1) {
			// An interaction's latency grows as later entries arrive, and it replaces itself rather
			// than taking a second place.
			toBeatMs = this.records[previousIndex].durationMs;
		} else if (this.records.length === MAX_RECORDS) {
			toBeatMs = this.records[MAX_RECORDS - 1].durationMs;
		}

		if (latencyMs <= toBeatMs) {
			return;
		}

		// Read only once the interaction has earned a place: naming its target walks the DOM, and
		// this runs while the page is already slow.
		const record = this.toRecord(interaction);

		if (previousIndex === -1) {
			this.records.push(record);
		} else {
			this.records[previousIndex] = record;
		}

		this.records.sort((a, b) => b.durationMs - a.durationMs);
		this.records.splice(MAX_RECORDS);
	}

	/** @returns the records, slowest first, or nothing when no interaction was slow enough. */
	snapshot(): SlowInteraction[] | undefined {
		if (this.records.length === 0) {
			return undefined;
		}

		return this.records.map((record) => ({
			group: record.group,
			name: record.name,
			durationMs: record.durationMs,
			inputDelayMs: record.inputDelayMs,
			processingMs: record.processingMs,
			presentationDelayMs: record.presentationDelayMs,
			target: record.target,
		}));
	}

	/**
	 * The phases are reported together or not at all: a browser that reports one reports all three,
	 * and zeroes standing in for values we never had would read as an interaction that spent no time
	 * anywhere. Each is clamped, because they come from timestamps the browser coarsens
	 * independently, so a phase can come out just below zero.
	 */
	private toRecord({
		entry,
		group,
		interactionId,
		latencyMs,
	}: ObservedInteraction): RecordedInteraction {
		const { processingStart, processingEnd, startTime } = entry;
		const measured = typeof processingStart === 'number' && typeof processingEnd === 'number';
		const phaseMs = (durationMs: number) => Math.max(0, Math.round(durationMs));

		return {
			interactionId,
			group,
			name: entry.name,
			durationMs: Math.round(latencyMs),
			inputDelayMs: measured ? phaseMs(processingStart - startTime) : undefined,
			processingMs: measured ? phaseMs(processingEnd - processingStart) : undefined,
			presentationDelayMs: measured ? phaseMs(startTime + latencyMs - processingEnd) : undefined,
			target: this.describeTarget(entry.target),
		};
	}

	/**
	 * Names the element an interaction happened on — `div[data-vc="x"] > p > span`, outermost first.
	 * The path climbs until an element carries an allow-listed attribute, because that is what says
	 * which part of the page this was.
	 */
	private describeTarget(node: Node | null | undefined): string | undefined {
		// An event's target can be a text node, and the element around it is the answer for it.
		let element: Element | null = node instanceof Element ? node : (node?.parentElement ?? null);

		const path: string[] = [];

		for (let climbed = 0; element && climbed < MAX_TARGET_ELEMENTS; climbed += 1) {
			const attribute = this.identifyingAttribute(element);
			path.unshift(`${element.localName}${attribute ?? ''}`);

			if (attribute) {
				break;
			}

			element = element.parentElement;
		}

		if (path.length === 0) {
			return undefined;
		}

		return path.join(' > ').slice(0, MAX_TARGET_LENGTH);
	}

	private identifyingAttribute(element: Element): string | undefined {
		for (const attribute of ALLOWED_TARGET_ATTRIBUTES) {
			const value = element.getAttribute(attribute);
			if (value) {
				// Encoded and cut: a value we did not write cannot bring quotes or a paragraph of
				// text into the event.
				const safeValue = encodeURIComponent(value).slice(0, MAX_ATTRIBUTE_VALUE_LENGTH);
				return `[${attribute}="${safeValue}"]`;
			}
		}

		return undefined;
	}
}
